/**
 * compatibility.ts — the boundary declaration, checked rather than asserted.
 *
 * `docs/architecture/browser-repository-boundary.md` requires the browser to
 * declare which API versions it supports and to fail fast on a mismatch:
 *
 *   > If the API version is outside the supported range, the browser must
 *   > refuse to operate rather than degrade unpredictably.
 *
 * A client that silently talks to an API it does not understand does not fail —
 * it produces wrong pictures of real plants. So a mismatch stops the
 * application at startup with a message naming both versions.
 */

export interface SemanticVersion { major: number; minor: number; patch: number }

export function parseVersion(text: string): SemanticVersion | undefined {
  const match = /^(\d+)\.(\d+)\.(\d+)/.exec(text.trim());
  if (!match) return undefined;
  return { major: Number(match[1]), minor: Number(match[2]), patch: Number(match[3]) };
}

function compare(a: SemanticVersion, b: SemanticVersion): number {
  return a.major - b.major || a.minor - b.minor || a.patch - b.patch;
}

/**
 * Evaluate a range of the form `>=1.0 <2.0`.
 *
 * Deliberately not a semver library: the boundary document specifies exactly
 * one range shape, and a dependency that can evaluate range grammars this
 * repository never writes is a dependency with more behaviour than the rule it
 * enforces.
 */
export function satisfies(version: string, range: string): boolean {
  const parsed = parseVersion(version);
  if (!parsed) return false;

  for (const clause of range.trim().split(/\s+/)) {
    const match = /^(>=|<=|>|<|=)?(\d+)(?:\.(\d+))?(?:\.(\d+))?$/.exec(clause);
    if (!match) return false;
    const bound: SemanticVersion = {
      major: Number(match[2]),
      minor: match[3] === undefined ? 0 : Number(match[3]),
      patch: match[4] === undefined ? 0 : Number(match[4]),
    };
    const order = compare(parsed, bound);
    switch (match[1] ?? "=") {
      case ">=": if (order < 0) return false; break;
      case ">":  if (order <= 0) return false; break;
      case "<=": if (order > 0) return false; break;
      case "<":  if (order >= 0) return false; break;
      default:   if (order !== 0) return false; break;
    }
  }
  return true;
}

export interface CompatibilityVerdict {
  compatible: boolean;
  apiVersion: string;
  supportedRange: string;
  reason?: string;
}

export function checkApiCompatibility(apiVersion: string, supportedRange: string): CompatibilityVerdict {
  if (!parseVersion(apiVersion)) {
    return { compatible: false, apiVersion, supportedRange, reason: `The server reported "${apiVersion}", which is not a version this client can compare.` };
  }
  if (!satisfies(apiVersion, supportedRange)) {
    return { compatible: false, apiVersion, supportedRange, reason: `This workbench supports API ${supportedRange}; the server serves ${apiVersion}. Upgrade whichever is behind — it will not guess.` };
  }
  return { compatible: true, apiVersion, supportedRange };
}
